import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme';
import { useLanguage } from '../lib/language';

const getTextShadowLight = (theme: { mood: string }) =>
  theme.mood === 'light'
    ? '0 1px 1px rgba(17,24,39,0.08)'
    : '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)';
const getBgColor = (theme: { mood: string }, opacity: number = 0.4) => {
  const o = Math.max(0, Math.min(1, opacity * 0.82));
  return theme.mood === 'light' ? `rgba(255, 255, 255, ${o})` : `rgba(0, 0, 0, ${o})`;
};
const getBorderColor = (theme: { mood: string }, opacity: number = 0.1) =>
  theme.mood === 'light' ? `rgba(0, 0, 0, ${opacity})` : `rgba(255, 255, 255, ${opacity})`;

const Studio: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const textShadowLight = getTextShadowLight(theme);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = language === 'es'
    ? [
        { title: 'Separación comercial', description: 'ERP, Productos AI, Páginas Web y Marketplace viven como líneas claras para vender sin confundir alcance ni promesa.' },
        { title: 'Operación real', description: 'Diseñamos pantallas, datos e integraciones pensando en uso diario: ventas, inventario, consultas, permisos y reportes.' },
        { title: 'Soporte y evolución', description: 'Después del lanzamiento mantenemos estabilidad y abrimos nuevas fases sin meter todo en un único proyecto gigante.' },
      ]
    : [
        { title: 'Commercial separation', description: 'ERP, AI Products, Websites, and Marketplace live as clear lines so the offer can be sold without scope confusion.' },
        { title: 'Real operations', description: 'We design screens, data, and integrations for daily use: sales, inventory, queries, roles, and reports.' },
        { title: 'Support and evolution', description: 'After launch, we keep stability and open new phases without forcing everything into one giant project.' },
      ];

  return (
    <main className="min-h-screen">
      {/* Hero — alineado con landing */}
      <section className="pt-28 sm:pt-32 pb-20 sm:pb-24 px-4 sm:px-6 lg:px-16">
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span
              className="text-[11px] uppercase tracking-[0.28em] mb-4 block"
              style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'Estudio' : 'About'}
            </span>
            <h1
              className="font-pixel text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6"
              style={{ color: 'var(--theme-text-primary)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'Estudio de sistemas comerciales' : 'Commercial Systems Studio'}
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: 'var(--theme-text-secondary)', opacity: 0.9, textShadow: textShadowLight }}
            >
              {language === 'es'
                ? 'Construimos software para empresas que necesitan ordenar operación, vender mejor y automatizar con criterio: ERP, Productos AI, Páginas Web y Marketplaces verticales.'
                : 'We build software for companies that need to organize operations, sell better, and automate with judgment: ERP, AI Products, Websites, and vertical Marketplaces.'}
              {' '}
              <Link to="/works" className="underline hover:opacity-80">{language === 'es' ? 'Ver proyectos' : 'See our work'}</Link>
              {language === 'es' ? ' o ' : ' or '}
              <Link to="/contact" className="underline hover:opacity-80">{language === 'es' ? 'contactar' : 'get in touch'}</Link>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section
        className="py-20 sm:py-24 border-t px-4 sm:px-6 lg:px-16"
        style={{ borderColor: getBorderColor(theme, 0.1) }}
      >
        <div className="max-w-[1600px] mx-auto">
          <div
            className="rounded-2xl border p-6 sm:p-8 backdrop-blur-sm"
            style={{ backgroundColor: getBgColor(theme, 0.5), borderColor: getBorderColor(theme, 0.12) }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
              <div className="lg:col-span-4">
                <span
                  className="text-[11px] uppercase tracking-[0.28em] mb-4 block"
                  style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
                >
                  {language === 'es' ? 'Filosofía' : 'Philosophy'}
                </span>
                <h2
                  className="font-pixel text-2xl sm:text-3xl md:text-4xl font-black tracking-tight"
                  style={{ color: 'var(--theme-text-primary)', textShadow: textShadowLight }}
                >
                  {language === 'es' ? 'Cómo entregamos' : 'How we deliver'}
                </h2>
              </div>
              <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {values.map((value, idx) => (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                    className="space-y-3"
                  >
                    <h3
                      className="font-pixel text-lg font-bold"
                      style={{ color: 'var(--theme-text-primary)', textShadow: textShadowLight }}
                    >
                      {value.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: 'var(--theme-text-secondary)', opacity: 0.85 }}
                    >
                      {value.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section
        className="py-20 sm:py-24 px-4 sm:px-6 lg:px-16 border-t"
        style={{ borderColor: getBorderColor(theme, 0.1), backgroundColor: getBgColor(theme, 0.25) }}
      >
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <span
              className="text-[11px] uppercase tracking-[0.28em] mb-4 block"
              style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'Qué hacemos' : 'What we do'}
            </span>
            <h2
              className="font-pixel text-2xl sm:text-3xl md:text-4xl font-black tracking-tight mb-4"
              style={{ color: 'var(--theme-text-primary)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'De la línea comercial al sistema funcionando' : 'From commercial line to working system'}
            </h2>
            <p
              className="text-base sm:text-lg leading-relaxed"
              style={{ color: 'var(--theme-text-secondary)', opacity: 0.9 }}
            >
              {language === 'es'
                ? 'Separamos el proyecto antes de construir: una web para vender, un ERP para operar, un producto AI para automatizar o un marketplace para conectar oferta y demanda. El detalle está en la '
                : 'We separate the project before building: a website to sell, an ERP to operate, an AI product to automate, or a marketplace to connect supply and demand. The full scope is on the '}
              <Link to="/" className="underline hover:opacity-80">{language === 'es' ? 'página de inicio' : 'home page'}</Link>;
              {language === 'es' ? ' aquí el foco es cómo trabajamos.' : ' here we focus on how we work.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tech stack */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-16 border-t" style={{ borderColor: getBorderColor(theme, 0.1) }}>
        <div className="max-w-[1600px] mx-auto">
          <span
            className="text-[11px] uppercase tracking-[0.28em] mb-6 block"
            style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
          >
            Tech Stack
          </span>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {[
              'React', 'Next.js', 'TypeScript', 'Node.js', 'PostgreSQL',
              'OpenAI', 'LangChain', 'Python', 'Vite', 'AWS',
              'React Native', 'Prisma', 'GraphQL', 'REST APIs', 'Docker',
              'Redis', 'REST', 'Power BI', 'Tableau',
            ].map((cap, idx) => (
              <motion.span
                key={cap}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.015 }}
                className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80"
                style={{
                  color: 'var(--theme-text-secondary)',
                  borderColor: getBorderColor(theme, 0.2),
                  backgroundColor: getBgColor(theme, 0.2),
                }}
              >
                {cap}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 sm:py-24 px-4 sm:px-6 lg:px-16 border-t"
        style={{ borderColor: getBorderColor(theme, 0.12) }}
      >
        <div className="max-w-[1600px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2
              className="font-pixel text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-4"
              style={{ color: 'var(--theme-text-primary)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'Empezar un proyecto' : 'Start a project'}
            </h2>
            <p
              className="text-base sm:text-lg mb-8"
              style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'Cuéntanos tu idea y definimos los siguientes pasos.' : "Tell us your idea and we'll figure out the next steps together."}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] rounded-full transition-opacity hover:opacity-90"
              style={{
                backgroundColor: 'var(--theme-btn-primary-bg)',
                color: 'var(--theme-btn-primary-text)',
              }}
            >
              {language === 'es' ? 'Contactar' : 'Get in Touch'}
              <span className="material-symbols-outlined text-base">arrow_forward</span>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Studio;
